import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, query, where, Timestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

// 管理者がアップロードし、同じ会社の従業員なら誰でも閲覧できるライブラリー。
// ファイル本体はFirebase Storage（companyDocs/{companyId}/...）に置き、
// タイトルなどのメタ情報はFirestoreの companyDocs コレクションに置く。

const CATEGORIES = ["就業規則", "規程・マニュアル", "様式・書式", "社内資料", "その他"];

const CAT_COLOR = {
  "就業規則":         "#c0392b",
  "規程・マニュアル": "#2980b9",
  "様式・書式":       "#8e44ad",
  "社内資料":         "#16a085",
  "その他":           "#7f8c8d",
};

// 1ファイルの上限（20MB）
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const EMPTY_FORM = { title: "", category: "就業規則", description: "" };

function CompanyDocs({ user, isAdmin, companyId, isPaid = null, isCompanyAdmin = false, onNavigate }) {
  const [files, setFiles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState("list");
  const [filter, setFilter]       = useState("すべて");
  const [form, setForm]           = useState(EMPTY_FORM);
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [error, setError]         = useState("");
  const fileInputRef = useRef();

  useEffect(() => { if (isPaid === true) fetchFiles(); else setLoading(false); }, [isPaid]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const q = companyId
        ? query(collection(db, "companyDocs"), where("companyId", "==", companyId))
        : collection(db, "companyDocs");
      const snap = await getDocs(q);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.createdAt - a.createdAt);
      // 閲覧用URLはその都度Storageから取得する（Firestoreには保存しない）
      const withUrl = await Promise.all(rows.map(async (r) => {
        try { return { ...r, url: await getDownloadURL(storageRef(storage, r.storagePath)) }; }
        catch (e) { console.warn("URLの取得に失敗しました:", r.storagePath, e); return { ...r, url: null }; }
      }));
      setFiles(withUrl);
    } catch (e) {
      console.error("ライブラリーの取得に失敗しました:", e);
      setError("資料の読み込みに失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setError("");
    if (f.size > MAX_FILE_SIZE) {
      setError("ファイルは20MB以下にしてください");
      e.target.value = "";
      return;
    }
    setFile(f);
    if (!form.title) setForm(prev => ({ ...prev, title: f.name.replace(/\.[^.]+$/, "") }));
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPaid)    { setError("この機能は有料プランでご利用いただけます。"); return; }
    if (!companyId) { setError("会社が設定されていないためアップロードできません。"); return; }
    if (!file)      { setError("ファイルを選択してください。"); return; }

    setUploading(true);
    setProgress(0);
    try {
      const safeName = file.name.replace(/[\\/:*?"<>|#]/g, "_");
      const path = `companyDocs/${companyId}/${Date.now()}_${safeName}`;
      const task = uploadBytesResumable(storageRef(storage, path), file, {
        contentType: file.type || "application/octet-stream",
      });
      await new Promise((resolve, reject) => {
        task.on("state_changed",
          (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve
        );
      });

      await addDoc(collection(db, "companyDocs"), {
        companyId,
        title:       form.title || file.name,
        category:    form.category,
        description: form.description,
        fileName:    file.name,
        fileType:    file.type || "",
        fileSize:    file.size,
        storagePath: path,
        uploadedBy:  user.email,
        createdAt:   Timestamp.now(),
      });

      setForm(EMPTY_FORM);
      removeFile();
      setTab("list");
      fetchFiles();
    } catch (err) {
      console.error("アップロードに失敗しました:", err);
      // 原因の切り分けができるよう、Firebaseが返したコードをそのまま出す
      const code = err?.code || "不明";
      const hint = code === "storage/unauthorized"
        ? "（Storageのルールで拒否されました）"
        : code === "storage/unknown"
          ? "（通信またはCORSで失敗した可能性があります）"
          : "";
      setError(`アップロードに失敗しました。[${code}] ${hint} ${err?.message || ""}`);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`「${f.title}」を削除しますか？`)) return;
    try {
      // 先にファイル本体を消す。既に無い場合は無視して一覧側だけ消す。
      try { await deleteObject(storageRef(storage, f.storagePath)); }
      catch (e) { console.warn("ファイル本体の削除に失敗しました（一覧のみ削除します）:", e); }
      await deleteDoc(doc(db, "companyDocs", f.id));
      fetchFiles();
    } catch (e) {
      console.error("削除に失敗しました:", e);
      alert("削除に失敗しました。");
    }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const fmtDate = (ts) => ts?.toDate().toLocaleDateString("ja-JP");
  const fmtSize = (n) => !n ? "" : n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.round(n / 1024)}KB`;

  const fileIcon = (type = "", name = "") => {
    const t = `${type} ${name}`.toLowerCase();
    if (t.includes("pdf")) return "📕";
    if (t.includes("image") || /\.(jpg|jpeg|png|gif)$/.test(t)) return "🖼️";
    if (t.includes("word") || /\.docx?$/.test(t)) return "📝";
    if (t.includes("sheet") || t.includes("excel") || /\.xlsx?$/.test(t)) return "📊";
    return "📎";
  };

  const shown = filter === "すべて" ? files : files.filter(f => f.category === filter);
  const usedCategories = ["すべて", ...CATEGORIES.filter(c => files.some(f => f.category === c))];

  // 無料プランではこの機能自体を使えない
  if (isPaid === false) {
    return (
      <div style={st.container}>
        <h2 style={st.title}>📚 ライブラリー</h2>
        <div style={st.lockBox}>
          <div style={{ fontSize: "44px", marginBottom: "12px" }}>🔒</div>
          <h3 style={{ color: "#2c3e50", margin: "0 0 10px" }}>有料プラン限定の機能です</h3>
          <p style={{ color: "#7f8c8d", lineHeight: "1.8", margin: "0 0 20px" }}>
            就業規則やマニュアルを従業員に配布できる機能です。<br />
            有料プランにご登録いただくとご利用いただけます。
          </p>
          {isCompanyAdmin && onNavigate && (
            <button style={st.upgradeBtn} onClick={() => onNavigate("billing")}>お支払い設定を開く</button>
          )}
          {!isCompanyAdmin && (
            <p style={{ color: "#95a5a6", fontSize: "13px", margin: 0 }}>会社の管理者にお問い合わせください。</p>
          )}
        </div>
      </div>
    );
  }

  if (isPaid === null) {
    return <div style={st.container}><p style={st.empty}>読み込み中...</p></div>;
  }

  return (
    <div style={st.container}>
      <h2 style={st.title}>📚 ライブラリー</h2>
      <p style={st.lead}>就業規則や各種マニュアルを閲覧できます。社員は全員閲覧できます。</p>

      <div style={st.tabs}>
        <button style={{ ...st.tab, ...(tab === "list" ? st.active : {}) }} onClick={() => setTab("list")}>一覧</button>
        {isAdmin && (
          <button style={{ ...st.tab, ...(tab === "upload" ? st.active : {}) }} onClick={() => setTab("upload")}>アップロード</button>
        )}
      </div>

      {tab === "upload" && isAdmin && (
        <form onSubmit={handleUpload} style={st.form}>
          <label style={st.label}>分類</label>
          <div style={st.catGrid}>
            {CATEGORIES.map(c => (
              <button key={c} type="button"
                style={{ ...st.catBtn, ...(form.category === c ? { backgroundColor: CAT_COLOR[c], border: `2px solid ${CAT_COLOR[c]}`, color: "#fff" } : {}) }}
                onClick={() => set("category", c)}
              >{c}</button>
            ))}
          </div>

          <label style={st.label}>タイトル</label>
          <input style={st.input} type="text" value={form.title}
            onChange={e => set("title", e.target.value)}
            placeholder="例：就業規則（2026年4月改定）" required />

          <label style={st.label}>説明 <span style={st.optional}>（任意）</span></label>
          <textarea style={{ ...st.input, height: "80px", resize: "vertical" }} value={form.description}
            onChange={e => set("description", e.target.value)}
            placeholder="従業員へのひとことがあれば入力してください" />

          <label style={st.label}>ファイル <span style={st.optional}>（20MB以下）</span></label>
          {file ? (
            <div style={st.filePreview}>
              <span style={{ fontSize: "20px" }}>{fileIcon(file.type, file.name)}</span>
              <span style={{ flex: 1, fontSize: "14px", color: "#2c3e50", wordBreak: "break-all" }}>
                {file.name} <span style={{ color: "#999" }}>（{fmtSize(file.size)}）</span>
              </span>
              <button type="button" style={st.fileRemoveBtn} onClick={removeFile} disabled={uploading}>✕ 削除</button>
            </div>
          ) : (
            <label style={st.fileLabel}>
              <span style={{ fontSize: "20px" }}>📎</span>
              <span style={{ fontSize: "14px", color: "#2c3e50" }}>ファイルを選択</span>
              <span style={{ fontSize: "12px", color: "#999" }}>PDF・Word・Excel・画像 など</span>
              <input ref={fileInputRef} type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt"
                style={{ display: "none" }} onChange={handleSelectFile} />
            </label>
          )}

          {uploading && (
            <div style={st.progressWrap}>
              <div style={{ ...st.progressBar, width: `${progress}%` }} />
              <span style={st.progressText}>{progress}%</span>
            </div>
          )}
          {error && <p style={st.error}>{error}</p>}

          <button style={st.submitBtn} type="submit" disabled={uploading}>
            {uploading ? "アップロード中..." : "アップロードする"}
          </button>
        </form>
      )}

      {tab === "list" && (
        <>
          {usedCategories.length > 1 && (
            <div style={st.filterRow}>
              {usedCategories.map(c => (
                <button key={c} type="button"
                  style={{ ...st.filterBtn, ...(filter === c ? { backgroundColor: "#2c3e50", border: "1px solid #2c3e50", color: "#fff" } : {}) }}
                  onClick={() => setFilter(c)}
                >{c}</button>
              ))}
            </div>
          )}

          {error && <p style={st.error}>{error}</p>}
          {loading
            ? <p style={st.empty}>読み込み中...</p>
            : shown.length === 0
              ? <p style={st.empty}>{isAdmin ? "資料はまだありません。「アップロード」から追加してください。" : "閲覧できる資料はまだありません"}</p>
              : shown.map(f => (
                <div key={f.id} style={{ ...st.card, borderLeft: `4px solid ${CAT_COLOR[f.category] || "#7f8c8d"}` }}>
                  <div style={st.cardTop}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <span style={{ ...st.catBadge, backgroundColor: CAT_COLOR[f.category] || "#7f8c8d" }}>{f.category}</span>
                      <h3 style={st.docTitle}>{fileIcon(f.fileType, f.fileName)} {f.title}</h3>
                    </div>
                    {isAdmin && <button style={st.deleteBtn} onClick={() => handleDelete(f)}>削除</button>}
                  </div>
                  {f.description && <p style={st.desc}>{f.description}</p>}
                  <div style={st.actionRow}>
                    {f.url
                      ? <a style={st.openBtn} href={f.url} target="_blank" rel="noreferrer">開く・保存する</a>
                      : <span style={{ fontSize: "13px", color: "#e74c3c" }}>ファイルを開けませんでした</span>}
                    <span style={st.fileMeta}>{f.fileName}{f.fileSize ? `（${fmtSize(f.fileSize)}）` : ""}</span>
                  </div>
                  <div style={st.meta}>掲載日：{fmtDate(f.createdAt)} · {f.uploadedBy}</div>
                </div>
              ))
          }
        </>
      )}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "6px" },
  lead: { color: "#7f8c8d", fontSize: "14px", marginTop: 0, marginBottom: "24px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "24px" },
  tab: { padding: "10px 20px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d" },
  active: { border: "2px solid #2c3e50", color: "#2c3e50", fontWeight: "bold" },
  form: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
  label: { display: "block", marginBottom: "8px", color: "#2c3e50", fontSize: "14px", fontWeight: "bold" },
  optional: { fontWeight: "normal", color: "#999", fontSize: "12px" },
  input: { width: "100%", padding: "12px", marginBottom: "16px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  catGrid: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" },
  catBtn: { padding: "8px 16px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d", fontWeight: "500" },
  fileLabel: { display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", border: "2px dashed #ccd", borderRadius: "10px", padding: "20px", cursor: "pointer", backgroundColor: "#fafbfc", marginBottom: "16px", textAlign: "center" },
  filePreview: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", backgroundColor: "#f0f8f4", borderRadius: "8px", marginBottom: "16px", border: "1px solid #c3e6cb" },
  fileRemoveBtn: { padding: "4px 12px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "6px", cursor: "pointer", fontSize: "12px", flexShrink: 0 },
  progressWrap: { position: "relative", height: "22px", backgroundColor: "#f0f4f8", borderRadius: "11px", overflow: "hidden", marginBottom: "16px" },
  progressBar: { height: "100%", backgroundColor: "#27ae60", transition: "width 0.2s" },
  progressText: { position: "absolute", top: 0, left: 0, right: 0, lineHeight: "22px", textAlign: "center", fontSize: "12px", color: "#2c3e50", fontWeight: "bold" },
  submitBtn: { padding: "12px 32px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  filterRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" },
  filterBtn: { padding: "6px 16px", border: "1px solid #ddd", borderRadius: "20px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  empty: { color: "#999", textAlign: "center", marginTop: "40px" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "12px" },
  card: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", gap: "10px" },
  catBadge: { padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" },
  docTitle: { color: "#2c3e50", margin: 0, fontSize: "17px" },
  desc: { color: "#555", fontSize: "14px", lineHeight: "1.7", margin: "0 0 10px", whiteSpace: "pre-wrap" },
  actionRow: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "8px" },
  openBtn: { display: "inline-block", padding: "8px 20px", backgroundColor: "#2980b9", color: "#fff", borderRadius: "8px", textDecoration: "none", fontSize: "14px" },
  fileMeta: { fontSize: "13px", color: "#7f8c8d", wordBreak: "break-all" },
  meta: { fontSize: "12px", color: "#bbb" },
  deleteBtn: { padding: "4px 12px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", flexShrink: 0 },
  lockBox: { backgroundColor: "#fff", padding: "40px 32px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textAlign: "center" },
  upgradeBtn: { padding: "12px 32px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
};

export default CompanyDocs;
